/**
 * SCWT-PM-V2 Phase 4 — PM Book tab.
 *
 * Renders inside the PropertyManagerDashboard tabs strip when tab==="book".
 * Property picker → test picker → recipient toggle → send payment request.
 * Below the form, the list of sent requests with actions per state.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Building2, Send, Loader2, Copy, RefreshCw, XCircle, RotateCcw,
  Mail, ShieldAlert,
} from "lucide-react";
import api from "@/lib/api";

const STATUS_META = {
  awaiting_payment:      { label: "Awaiting payment",   tone: "amber" },
  declined:              { label: "Declined",           tone: "slate" },
  expired:               { label: "Expired",            tone: "slate" },
  cancelled:             { label: "Cancelled",          tone: "slate" },
  payment_received:      { label: "Payment received",   tone: "emerald" },
  conversion_pending:    { label: "Preparing booking",  tone: "cyan" },
  conversion_failed:     { label: "Booking issue",      tone: "rose" },
  converted_to_booking:  { label: "Booking created",    tone: "emerald" },
};

export default function PmBookTab() {
  const [properties, setProperties] = useState([]);
  const [testCatalog, setTestCatalog] = useState([]);
  const [requests, setRequests]     = useState([]);
  const [loading, setLoading]       = useState(true);

  // form state
  const [propertyId, setPropertyId] = useState("");
  const [testId, setTestId]         = useState("");
  const [recipientType, setRecipientType]   = useState("landlord");
  const [tenantConfirmed, setTenantConfirmed] = useState(false);
  const [recipientName, setRecipientName]   = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSource, setEmailSource]       = useState("property_record");
  const [pmMessage, setPmMessage]           = useState("");
  const [busy, setBusy]                     = useState(false);
  const [error, setError]                   = useState(null);
  const [duplicateWarn, setDuplicateWarn]   = useState(null);
  const [lastPayUrl, setLastPayUrl]         = useState(null);

  const chosenProperty = useMemo(
    () => properties.find((p) => p.id === propertyId),
    [properties, propertyId]
  );

  const loadCatalog = async (pid) => {
    if (!pid) { setTestCatalog([]); return; }
    try {
      const { data } = await api.get(
        `/property-manager/landlord-payment-requests/test-catalog?property_id=${pid}`
      );
      setTestCatalog(data.items || []);
      // Default to the recommended test if no test is selected yet
      const rec = (data.items || []).find((t) => t.recommended);
      if (rec) setTestId(rec.test_id);
    } catch { setTestCatalog([]); }
  };

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/property-manager/landlord-payment-requests");
      setRequests(data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          api.get("/property-manager/properties"),
          api.get("/property-manager/landlord-payment-requests"),
        ]);
        setProperties(p.data?.items || p.data || []);
        setRequests(r.data?.items || []);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    loadCatalog(propertyId);
    if (chosenProperty) {
      // Pre-fill recipient from property record based on toggle
      applyRecipientDefaults(chosenProperty, recipientType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const applyRecipientDefaults = (prop, type) => {
    if (!prop) return;
    if (type === "landlord") {
      setRecipientName(prop.owner_name || "");
      setRecipientEmail(prop.owner_email || "");
      setEmailSource("property_record");
    } else {
      setRecipientName(prop.tenant_name || "");
      setRecipientEmail(prop.tenant_email || "");
      setEmailSource("property_record");
    }
  };

  const onRecipientTypeChange = (type) => {
    setRecipientType(type);
    if (type === "landlord") setTenantConfirmed(false);
    applyRecipientDefaults(chosenProperty, type);
  };

  const onEmailEdit = (val) => {
    setRecipientEmail(val);
    setEmailSource("manual_override");
  };

  const submit = async ({ overrideDuplicate = false } = {}) => {
    setBusy(true); setError(null); setLastPayUrl(null);
    try {
      const { data } = await api.post(
        "/property-manager/landlord-payment-requests",
        {
          property_id: propertyId,
          test_id: testId,
          recipient_type: recipientType,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim(),
          recipient_email_source: emailSource,
          tenant_authorisation_confirmed: recipientType === "tenant" ? tenantConfirmed : false,
          optional_pm_message: pmMessage.trim() || undefined,
          duplicate_override_confirmed: overrideDuplicate,
        }
      );
      setLastPayUrl(data.pay_link_url);
      setDuplicateWarn(null);
      // Reset for next
      setPmMessage("");
      loadRequests();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "duplicate_active_request") {
        setDuplicateWarn(detail);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Could not send request. Please try again.");
      }
    } finally { setBusy(false); }
  };

  const canSubmit = useMemo(() => {
    if (!propertyId || !testId || !recipientName.trim() || !recipientEmail.trim()) return false;
    if (recipientType === "tenant" && !tenantConfirmed) return false;
    return !busy;
  }, [propertyId, testId, recipientName, recipientEmail, recipientType, tenantConfirmed, busy]);

  const copy = (url) => { try { navigator.clipboard.writeText(url); } catch { /* ignore */ } };

  const resend = async (ref) => {
    try {
      await api.post(`/property-manager/landlord-payment-requests/${ref}/resend`);
      loadRequests();
    } catch (e) { alert(e?.response?.data?.detail || "Resend failed"); }
  };
  const cancel = async (ref) => {
    const reason = window.prompt("Why are you cancelling this request?") || "";
    try {
      await api.post(`/property-manager/landlord-payment-requests/${ref}/cancel`, { reason });
      loadRequests();
    } catch (e) { alert(e?.response?.data?.detail || "Cancel failed"); }
  };
  const reactivate = async (ref) => {
    try {
      const { data } = await api.post(`/property-manager/landlord-payment-requests/${ref}/reactivate`);
      setLastPayUrl(data.pay_link_url);
      loadRequests();
    } catch (e) { alert(e?.response?.data?.detail || "Reactivate failed"); }
  };
  const requestCancellation = async (ref) => {
    const reason = window.prompt("Please describe why you'd like this cancelled (admin will review):") || "";
    if (!reason.trim()) return;
    try {
      await api.post(`/property-manager/landlord-payment-requests/${ref}/request-cancellation`, { reason });
      loadRequests();
    } catch (e) { alert(e?.response?.data?.detail || "Request failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;

  return (
    <div data-testid="pm-book-tab" className="space-y-6">
      {/* CREATE FORM */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900">Send a landlord-paid water testing request</h2>
        <p className="text-xs text-slate-500 mt-1">Aware sends the branded payment link. The landlord (or tenant, with your confirmation) pays directly. A booking is created only after payment.</p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Property</label>
            <select data-testid="pm-book-property"
                     value={propertyId}
                     onChange={(e) => setPropertyId(e.target.value)}
                     className={inp}>
              <option value="">Select a managed property…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.address?.street || p.street} · {p.address?.suburb || p.suburb}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Test</label>
            <select data-testid="pm-book-test"
                     value={testId}
                     onChange={(e) => setTestId(e.target.value)}
                     className={inp}
                     disabled={!propertyId}>
              <option value="">Select a test…</option>
              {testCatalog.map((t) => (
                <option key={t.test_id} value={t.test_id}>
                  {t.test_name} — {t.price_display}{t.recommended ? " (recommended)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5">
          <label className="block text-xs font-semibold text-slate-700 mb-2">Send request to</label>
          <div className="flex gap-2" data-testid="pm-book-recipient-toggle">
            <RecipientChip active={recipientType === "landlord"}
                            onClick={() => onRecipientTypeChange("landlord")}
                            testid="pm-book-recipient-landlord"
                            label="Landlord" />
            <RecipientChip active={recipientType === "tenant"}
                            onClick={() => onRecipientTypeChange("tenant")}
                            testid="pm-book-recipient-tenant"
                            label="Tenant" />
          </div>
          {recipientType === "tenant" && (
            <label className="mt-3 flex gap-2 items-start text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3 cursor-pointer">
              <input type="checkbox"
                      data-testid="pm-book-tenant-confirm"
                      checked={tenantConfirmed}
                      onChange={(e) => setTenantConfirmed(e.target.checked)}
                      className="mt-0.5" />
              <span>I confirm I am authorised to contact this tenant about water testing for this property.</span>
            </label>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Recipient name</label>
            <input data-testid="pm-book-name" value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    className={inp} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Recipient email
              {emailSource === "manual_override" && <span className="ml-2 text-[10px] uppercase text-amber-700">manual override</span>}
            </label>
            <input data-testid="pm-book-email" value={recipientEmail}
                    onChange={(e) => onEmailEdit(e.target.value)}
                    className={inp} type="email" />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-semibold text-slate-700 mb-1">Message (optional)</label>
          <textarea data-testid="pm-book-message"
                    value={pmMessage}
                    onChange={(e) => setPmMessage(e.target.value)}
                    rows={2} maxLength={500}
                    className={inp} />
        </div>

        {error && <div data-testid="pm-book-error" className="mt-4 text-sm text-rose-700">{error}</div>}
        {duplicateWarn && (
          <div data-testid="pm-book-duplicate-warn" className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-start gap-2 text-amber-900 text-sm">
              <ShieldAlert className="w-4 h-4 mt-0.5" />
              <div>
                An active payment request already exists for this property and test
                {duplicateWarn.existing_reference && <> (<strong>{duplicateWarn.existing_reference}</strong>)</>}.
                Create another request anyway?
                <div className="mt-3 flex gap-2">
                  <button data-testid="pm-book-duplicate-cancel" onClick={() => setDuplicateWarn(null)} className="text-xs text-slate-600 hover:text-slate-900">Cancel</button>
                  <button data-testid="pm-book-duplicate-confirm" onClick={() => submit({ overrideDuplicate: true })} className="text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1.5 font-semibold">Yes, create another</button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button data-testid="pm-book-submit"
                   disabled={!canSubmit}
                   onClick={() => submit()}
                   className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-800 disabled:bg-slate-300 text-white font-semibold rounded-full px-6 py-2.5 text-sm">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <Send className="w-4 h-4" /> Send payment request
          </button>
        </div>

        {lastPayUrl && (
          <div data-testid="pm-book-last-url" className="mt-5 rounded-xl bg-emerald-50 border border-emerald-100 p-4">
            <div className="text-xs font-semibold text-emerald-800 mb-2">Payment request sent. Copy the link if you'd like to share it manually too:</div>
            <div className="flex gap-2">
              <input readOnly value={lastPayUrl} className={`${inp} text-xs bg-white`} data-testid="pm-book-last-url-input" />
              <button data-testid="pm-book-last-url-copy" onClick={() => copy(lastPayUrl)} className="inline-flex items-center gap-1 border border-emerald-200 rounded-full px-3 py-2 text-xs text-emerald-800 hover:bg-emerald-100">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="mt-2 text-[11px] text-emerald-800">Do not post this link publicly — it is unique to this recipient.</div>
          </div>
        )}
      </section>

      {/* SENT REQUESTS LIST */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Sent requests</h3>
          <button data-testid="pm-book-refresh" onClick={loadRequests} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {requests.length === 0 ? (
          <div data-testid="pm-book-empty" className="p-8 text-center text-sm text-slate-500">No payment requests yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {requests.map((r) => (
              <li key={r.id} data-testid={`pm-book-row-${r.reference}`} className="px-6 py-4 flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                    {r.property_snapshot?.address?.street}, {r.property_snapshot?.address?.suburb}
                  </div>
                  <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1 mt-0.5">
                    <Mail className="w-3 h-3" /> {r.recipient?.normalised_email}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    {r.reference} · {r.test_snapshot?.test_name_snapshot} · ${((r.test_snapshot?.total_amount || 0)/100).toFixed(2)} · Expires {r.pay_link_expires_at?.slice(0,10)}
                  </div>
                </div>
                <StatusChip status={r.status} />
                <div className="flex flex-wrap items-center gap-2">
                  {r.status === "awaiting_payment" && <>
                    <ActionBtn onClick={() => resend(r.reference)} testid={`pm-book-resend-${r.reference}`} icon={<RefreshCw className="w-3 h-3" />}>Resend</ActionBtn>
                    <ActionBtn onClick={() => cancel(r.reference)} testid={`pm-book-cancel-${r.reference}`} icon={<XCircle className="w-3 h-3" />} danger>Cancel</ActionBtn>
                  </>}
                  {r.status === "expired" && (
                    <ActionBtn onClick={() => reactivate(r.reference)} testid={`pm-book-reactivate-${r.reference}`} icon={<RotateCcw className="w-3 h-3" />}>Reactivate</ActionBtn>
                  )}
                  {["converted_to_booking", "payment_received", "conversion_pending"].includes(r.status) && (
                    <ActionBtn onClick={() => requestCancellation(r.reference)} testid={`pm-book-request-cancel-${r.reference}`} icon={<XCircle className="w-3 h-3" />} danger>Request cancellation</ActionBtn>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

function RecipientChip({ active, onClick, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
              active ? "bg-cyan-700 text-white border-cyan-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}>{label}</button>
  );
}

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, tone: "slate" };
  const toneCls = {
    amber:   "bg-amber-100 text-amber-800",
    emerald: "bg-emerald-100 text-emerald-800",
    slate:   "bg-slate-100 text-slate-600",
    rose:    "bg-rose-100 text-rose-800",
    cyan:    "bg-cyan-100 text-cyan-800",
  }[meta.tone];
  return (
    <span data-testid={`pm-book-status-${status}`}
          className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-1 uppercase tracking-wide ${toneCls}`}>
      {meta.label}
    </span>
  );
}

function ActionBtn({ children, onClick, icon, danger, testid }) {
  const cls = danger
    ? "border-rose-200 text-rose-700 hover:bg-rose-50"
    : "border-slate-200 text-slate-700 hover:bg-slate-50";
  return (
    <button data-testid={testid} onClick={onClick}
            className={`text-xs border rounded-full px-3 py-1.5 inline-flex items-center gap-1 ${cls}`}>
      {icon} {children}
    </button>
  );
}
